import type { ReactNode } from "react"
import { useMemo } from "react"

import { useCabinetAccess, useHasPermission } from "../../../access/use-access"
import type { EmployeeView } from "../../../api/directory.api"
import { fetchEmployees } from "../../../api/directory.api"
import { fetchJournal } from "../../../api/journal.api"
import { cx } from "../../../lib/cx"
import { shiftDate, todayInZone } from "../../../lib/format"
import { servicesLabel } from "../../../lib/plural"
import { ProblemScreen } from "../../../shell/states"
import { resolveBranch } from "../../../shell/use-branch"
import { Badge, Button, IconButton, Skeleton } from "../../../ui"
import styles from "../directories.module.css"
import { CenterState, Note, StandHeader } from "../stand"
import type { Resource } from "../use-resource"
import { useResource } from "../use-resource"
import type { ScheduleDayKind, ScheduleRow, ScheduleWeekView } from "./schedule-model"
import { buildScheduleWeek, formatDayWarning, formatWeekRange, weekStart } from "./schedule-model"
import { InvitePanel } from "./invite-panel"
import { publicationBadge } from "./profile-model"

/**
 * Сотрудники (3-Б5) и график смен (3-Б8).
 *
 * ПОЧЕМУ ВМЕСТЕ И ПОЧЕМУ У ГРАФИКА ВСЁ-ТАКИ СВОЙ АДРЕС. Смену ставят, глядя на список
 * мастеров, а мастера заводят, глядя на дыры в графике: это одна работа, и разводить её
 * по двум экранам значит заставить держать половину в голове. Но «поставь смену на ту
 * неделю» — обычная просьба в чате, и у неё обязана быть ссылка. Поэтому экран один,
 * адреса два: /staff показывает обе половины, /staff/schedule — только график, и обе
 * читают неделю из адреса.
 *
 * ГЛАВНОЕ НА ЭКРАНЕ — НЕ ЧИСЛО, А ПРЕДУПРЕЖДЕНИЕ ПОД СЕТКОЙ: день, когда филиал открыт,
 * онлайн-запись работает, а на смене нет ни одного мастера. Клиент в такой день видит
 * пустой календарь и уходит, а салон узнаёт об этом из выручки месяцем позже.
 */

const DAY_KIND_STYLE: Readonly<Record<ScheduleDayKind, string | undefined>> = {
  shift: styles.cellShift,
  override: styles.cellOverride,
  timeoff: styles.cellTimeoff,
  holiday: styles.cellHoliday,
  none: styles.cellNone,
}

export function StaffScreen(props: {
  readonly week: string | null
  readonly onWeekChange: (week: string) => void
  readonly onEmployeeLeave: (employeeId: string) => void
  /** Публичный профиль мастера (3-Б7). Нет на экране «только график». */
  readonly onEmployeeProfile?: ((employeeId: string) => void) | undefined
  /** Только график: адрес /staff/schedule и узкие экраны. */
  readonly scheduleOnly?: boolean
}): ReactNode {
  const { context } = useCabinetAccess()
  const canManageStaff = useHasPermission("staff.manage")
  const canManageShifts = useHasPermission("staff.schedule.manage")
  const canViewAccess = useHasPermission("staff.view")
  const canInvite = useHasPermission("staff.invite")

  const branch = resolveBranch(context, null)
  const timeZone = branch?.timezone ?? context.tenant.defaultTimezone
  const today = todayInZone(timeZone)
  const start = weekStart(props.week ?? today)
  const end = shiftDate(start, 6)

  const employees = useResource(
    props.scheduleOnly === true ? null : (signal) => fetchEmployees(false, signal),
    props.scheduleOnly === true ? null : "staff-employees",
  )

  const journalQuery = useMemo(
    () =>
      branch === null
        ? null
        : { branchId: branch.id, dateFrom: start, dateTo: end, includeCancelled: false },
    [branch, start, end],
  )

  const schedule = useResource(
    journalQuery === null ? null : (signal) => fetchJournal(journalQuery, signal),
    journalQuery === null ? null : `${journalQuery.branchId}|${start}`,
  )

  const week = useMemo(
    () => (schedule.data === null ? null : buildScheduleWeek(schedule.data, start, today)),
    [schedule.data, start, today],
  )

  if (branch === null) {
    return (
      <div className={styles.screen}>
        <StandHeader
          title={props.scheduleOnly === true ? "График смен" : "Сотрудники"}
          repeatsSection={props.scheduleOnly !== true}
        />
        <CenterState
          title="Вам не открыт ни один филиал"
          text="Попросите владельца выдать доступ к филиалу — сотрудники и график появятся сразу."
        />
      </div>
    )
  }

  const panel = (
    <SchedulePanel
      week={week}
      start={start}
      schedule={schedule}
      canManageShifts={canManageShifts}
      onWeekChange={props.onWeekChange}
    />
  )

  if (props.scheduleOnly === true) {
    return (
      <div className={styles.screen}>
        <StandHeader title="График смен" count={formatWeekRange(start)} />
        <div className={styles.splitRight}>{panel}</div>
      </div>
    )
  }

  if (employees.problem !== null && employees.data === null) {
    return <ProblemScreen problem={employees.problem} onRetry={employees.reload} />
  }

  const list = employees.data ?? []
  const bookable = list.filter((employee) => employee.isBookable)
  const empty = employees.data !== null && list.length === 0

  // Приглашения видит тот, кому открыт список доступа (staff.view); пригласить — staff.invite.
  const invitePanel = canViewAccess ? (
    <InvitePanel
      branches={context.branches}
      employees={list}
      canInvite={canInvite}
      canManageAccess={canManageStaff}
      viewerUserId={context.user.id}
      timeZone={timeZone}
    />
  ) : null

  return (
    <div className={styles.screen}>
      <StandHeader title="Сотрудники" repeatsSection>
        {bookable.length === 0 ? null : (
          <Badge tone="info">Мастеров-исполнителей: {bookable.length}</Badge>
        )}
        <Button
          size="sm"
          deniedReason={
            canManageStaff ? undefined : "Заводить сотрудников может управляющий или владелец"
          }
        >
          Архивные
        </Button>
        <Button
          size="sm"
          variant="primary"
          deniedReason={
            canManageStaff ? undefined : "Заводить сотрудников может управляющий или владелец"
          }
        >
          + Сотрудник
        </Button>
      </StandHeader>

      {employees.loading ? (
        <div className={styles.cardList}>
          {[0, 1, 2, 3].map((row) => (
            <Skeleton key={row} width="100%" height={46} radius={10} />
          ))}
        </div>
      ) : empty ? (
        <>
        <CenterState
          title="Сотрудников пока нет"
          text="Добавьте хотя бы одного мастера — без исполнителя нельзя записать клиента."
          note="Мастер появится в расписании сразу. Доступ в кабинет — отдельно, он нужен не всем."
          actions={
            <>
              <Button
                variant="primary"
                deniedReason={
                  canManageStaff ? undefined : "Заводить мастеров может управляющий или владелец"
                }
              >
                Добавить мастера
              </Button>
              <Button
                deniedReason={
                  canManageStaff ? undefined : "Заводить мастеров может управляющий или владелец"
                }
              >
                Я работаю один
              </Button>
            </>
          }
        />
        {invitePanel}
        </>
      ) : (
        <div className={cx(styles.split, styles.splitHalves)}>
          <div className={styles.splitLeft}>
            <div className={styles.cardList}>
              {list.map((employee) => (
                <EmployeeRow
                  key={employee.id}
                  employee={employee}
                  canManage={canManageStaff}
                  onLeave={props.onEmployeeLeave}
                  onProfile={props.onEmployeeProfile}
                />
              ))}
            </div>
            {invitePanel}
          </div>

          <div className={styles.splitRight}>{panel}</div>
        </div>
      )}
    </div>
  )
}

function SchedulePanel(props: {
  readonly week: ScheduleWeekView | null
  readonly start: string
  readonly schedule: Resource<unknown>
  readonly canManageShifts: boolean
  readonly onWeekChange: (week: string) => void
}): ReactNode {
  const { week } = props

  return (
    <>
      <div className={styles.scheduleHead}>
        <span className={styles.sectionLabel}>График смен · {formatWeekRange(props.start)}</span>
        <div className={styles.headerActions}>
          <IconButton
            label="Предыдущая неделя"
            icon="‹"
            onClick={() => {
              props.onWeekChange(shiftDate(props.start, -7))
            }}
          />
          <IconButton
            label="Следующая неделя"
            icon="›"
            onClick={() => {
              props.onWeekChange(shiftDate(props.start, 7))
            }}
          />
        </div>
      </div>

      {props.schedule.problem !== null && week === null ? (
        <p className={styles.rowSub}>
          График не загрузился. Список сотрудников это не затронуло — повторите загрузку.
        </p>
      ) : week === null ? (
        <Skeleton width="100%" height={240} radius={8} />
      ) : (
        <>
          <div className={styles.scheduleGrid}>
            <div className={styles.scheduleCorner} />
            {week.days.map((day) => (
              <div
                key={day.date}
                className={cx(
                  styles.scheduleDay,
                  day.isToday && styles.scheduleToday,
                  !day.isToday && day.isSunday && styles.scheduleSunday,
                )}
              >
                {day.weekdayLabel}
                <br />
                {day.dayNumber}
              </div>
            ))}

            {week.rows.map((row) => (
              <ScheduleRowCells key={row.employeeId} row={row} />
            ))}
          </div>

          <Legend />

          {week.emptyOpenDays.map((day) => (
            <Note
              key={day.date}
              strong={`${formatDayWarning(
                day,
                week.days.map((item) => item.date),
              )}: в филиале не работает ни один мастер`}
            >
              , а онлайн-запись открыта. Клиент увидит пустой календарь.{" "}
              {props.canManageShifts ? (
                <button type="button" className={styles.inlineLink}>
                  Поставить смену
                </button>
              ) : (
                "Поставить смену может управляющий."
              )}
            </Note>
          ))}
        </>
      )}
    </>
  )
}

function ScheduleRowCells(props: { readonly row: ScheduleRow }): ReactNode {
  const { row } = props
  return (
    <>
      <div className={styles.scheduleName}>
        <span
          className={styles.scheduleNameDot}
          style={row.color === null ? undefined : { background: row.color }}
          aria-hidden="true"
        />
        {row.name}
      </div>
      {row.cells.map((cell) => (
        <div
          key={cell.date}
          className={cx(styles.cell, DAY_KIND_STYLE[cell.kind])}
          title={cell.title}
        >
          {/*
            Подпись клетки читается голосом целиком: «10–20 · из шаблона». Без неё
            человек, слушающий экран, получил бы ряд одинаковых «10–20», хотя глазами
            четыре вида дня различаются мгновенно.
          */}
          <span className="plnr-visually-hidden">{`${row.name}, ${cell.title}`}</span>
          <span aria-hidden="true">{cell.primary}</span>
          {cell.secondary.length === 0 ? null : <span aria-hidden="true">{cell.secondary}</span>}
        </div>
      ))}
    </>
  )
}

function Legend(): ReactNode {
  return (
    <div className={styles.legend}>
      <span>
        <span className={cx(styles.legendSwatch, styles.cellShift)} aria-hidden="true" />
        из шаблона
      </span>
      <span>
        <span className={cx(styles.legendSwatch, styles.cellOverride)} aria-hidden="true" />
        правка поверх шаблона
      </span>
      <span>
        <span className={cx(styles.legendSwatch, styles.cellTimeoff)} aria-hidden="true" />
        отсутствие
      </span>
      <span>
        <span className={cx(styles.legendSwatch, styles.cellHoliday)} aria-hidden="true" />
        праздник — подсказка, решает салон
      </span>
      <span>
        <span className={cx(styles.legendSwatch, styles.cellNone)} aria-hidden="true" />
        выходной
      </span>
    </div>
  )
}

function EmployeeRow(props: {
  readonly employee: EmployeeView
  readonly canManage: boolean
  readonly onLeave: (employeeId: string) => void
  readonly onProfile?: ((employeeId: string) => void) | undefined
}): ReactNode {
  const { employee } = props
  const publication = publicationBadge(employee)
  const servicesText =
    employee.serviceIds.length === 0
      ? employee.isBookable
        ? "услуги не отмечены"
        : "—"
      : servicesLabel(employee.serviceIds.length)

  return (
    <div className={cx(styles.rowItem, styles.rowStatic)}>
      <span
        className={styles.masterStripe}
        style={employee.color === null ? undefined : { background: employee.color }}
        aria-hidden="true"
      />
      <span className={styles.rowGrow}>
        {/* Имя ведёт в публичный профиль (3-Б7): то, как мастера видит клиент, правится
            там, а не в общей карточке. */}
        {props.onProfile === undefined ? (
          <span className={styles.rowTitle}>{employee.displayName}</span>
        ) : (
          <button
            type="button"
            className={cx(styles.rowTitle, styles.rowTitleLink)}
            onClick={() => props.onProfile?.(employee.id)}
          >
            {employee.displayName}
          </button>
        )}
        <span className={styles.rowSub}>
          {employee.positionName ?? "Без должности"} · {servicesText}
        </span>
      </span>

      {/* «В тарифе» и «бесплатно» — это деньги салона, и путать их нельзя:
          администратор и владелец исполнителями не являются и в тариф не входят.
          Значки — одной группой: на телефоне она уходит под имя целой строкой. */}
      <span className={styles.rowBadges}>
        <Badge tone={employee.isBookable ? "info" : "gray"}>
          {employee.isBookable ? "исполнитель · в тарифе" : "не исполнитель · бесплатно"}
        </Badge>
        {/* По тем же правилам, что у сервера: без публичного имени мастера на витрине нет
            (П2-00), и прежний значок «на витрине» у такого мастера был неправдой. */}
        {publication === null ? null : <Badge tone={publication.tone}>{publication.label}</Badge>}
      </span>

      <IconButton
        label={`Мастер выбывает: ${employee.displayName}`}
        icon="⋯"
        deniedReason={
          props.canManage ? undefined : "Выводить мастеров из смены может управляющий или владелец"
        }
        onClick={() => {
          props.onLeave(employee.id)
        }}
      />
    </div>
  )
}
